import { useTranslation } from "react-i18next";
import { authApi } from "../api/auth";
import { Select } from "../components/Select";
import { useAuth } from "../auth/AuthContext";
import { SUPPORTED_LOCALES, type SupportedLocale } from "./index";
import { flagEmoji } from "./countries";

const LANG_LABEL: Record<SupportedLocale, string> = {
  en: "English",
  sl: "Slovenščina",
  de: "Deutsch",
  hr: "Hrvatski",
  sr: "Srpski",
  it: "Italiano",
  es: "Español",
  fr: "Français",
  ru: "Русский",
  tr: "Türkçe",
};

// Locale → country whose flag we use. English maps to GB.
const LANG_FLAG: Record<SupportedLocale, string> = {
  en: "GB",
  sl: "SI",
  de: "DE",
  hr: "HR",
  sr: "RS",
  it: "IT",
  es: "ES",
  fr: "FR",
  ru: "RU",
  tr: "TR",
};

export function localeFlag(loc: SupportedLocale): string {
  return flagEmoji(LANG_FLAG[loc]);
}

export function localeLabel(loc: SupportedLocale): string {
  return LANG_LABEL[loc];
}

/** The OS emoji faces — the app font renders flags as letter pairs without them. */
const FLAG_FONT = 'apple color emoji, segoe ui emoji, noto color emoji, "Inter Tight", system-ui, sans-serif';

/** Locales available to Bankom-internal staff. Customers and suppliers see the full list. */
const INTERNAL_LOCALES: SupportedLocale[] = ["sr", "en"];

export function LocaleSwitcher() {
  const { i18n, t } = useTranslation();
  const { state } = useAuth();
  const current = (i18n.resolvedLanguage ?? "en") as SupportedLocale;

  // Policy: Bankom is a Serbian company, so staff get SR + EN only. The full
  // multi-language matrix is reserved for customer and supplier accounts.
  const visibleLocales = (state.status === "authenticated" && state.user.internal)
    ? INTERNAL_LOCALES
    : SUPPORTED_LOCALES;

  async function handleChange(next: SupportedLocale) {
    await i18n.changeLanguage(next);
    if (state.status === "authenticated") {
      try {
        await authApi.updateLocale(next);
      } catch {
        // Non-fatal: UI already switched, server will catch up on next change.
      }
    }
  }

  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
      <span className="locale-label">{t("language.label")}</span>
      <Select<SupportedLocale>
        value={current}
        onChange={(next) => { void handleChange(next); }}
        ariaLabel={t("language.label")}
        style={{ minWidth: 150 }}
        options={visibleLocales.map((loc) => ({
          value: loc,
          label: LANG_LABEL[loc],
          left: <span aria-hidden style={{ fontFamily: FLAG_FONT }}>{localeFlag(loc)}</span>,
        }))}
      />
    </label>
  );
}
